import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { Eye, EyeOff } from "@/shared/components/icons";
import { messages } from "@/shared/i18n";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Optional node rendered on the right side of the label row (e.g. a "Forgot password?" link) */
  labelRight?: ReactNode;
  error?: string | undefined;
  helperText?: string;
  fullWidth?: boolean;
  "data-testid"?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      labelRight,
      error,
      helperText,
      fullWidth = true,
      className,
      id,
      type,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isPasswordField = type === "password";
    const effectiveType = isPasswordField ? (isPasswordVisible ? "text" : "password") : type;

    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const hasError = Boolean(error);

    const handleTogglePasswordVisibility = () => {
      setIsPasswordVisible((prev) => !prev);
    };

    return (
      <div className={cn(fullWidth && "w-full")}>
        {(label ?? labelRight) && (
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700" htmlFor={inputId}>
              {label}
              {props.required && (
                <span aria-label="required" className="ml-0.5 text-red-600">
                  *
                </span>
              )}
            </label>
            {labelRight && <span>{labelRight}</span>}
          </div>
        )}
        <div className="relative">
          <input
            ref={ref}
            aria-describedby={hasError ? errorId : undefined}
            aria-invalid={hasError}
            className={cn(
              "control-md block w-full rounded-lg border shadow-sm transition-colors",
              "bg-white text-gray-900 placeholder:text-gray-400",
              "focus:ring-2 focus:outline-none",
              hasError
                ? "border-red-400 focus:border-red-400 focus:ring-red-400/20"
                : "focus:border-brand-primary focus:ring-brand-primary/20 border-gray-300",
              "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
              isPasswordField && "pr-10",
              className
            )}
            data-testid={dataTestId}
            id={inputId}
            type={effectiveType}
            {...props}
          />
          {isPasswordField && (
            <button
              aria-label={
                isPasswordVisible
                  ? messages.components.accessibility.hidePassword
                  : messages.components.accessibility.showPassword
              }
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1.5 text-gray-400 transition-colors hover:text-gray-600 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
              data-testid={dataTestId ? `${dataTestId}-toggle-password` : undefined}
              type="button"
              onClick={handleTogglePasswordVisibility}
            >
              {isPasswordVisible ? (
                <EyeOff aria-hidden className="h-5 w-5" />
              ) : (
                <Eye aria-hidden className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
        {(error ?? helperText) && (
          <p
            className={cn("mt-1 text-sm", hasError ? "text-red-600" : "text-gray-600")}
            data-testid={dataTestId ? `${dataTestId}-error` : undefined}
            id={errorId}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
